import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result as string).split(",")[1] ?? "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Signup() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", password2: "",
    company_name: "", business_no: "", representative: "", phone: "", address: "",
  });
  const [license, setLicense] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.password2) return setErr("비밀번호가 일치하지 않습니다.");
    if (form.password.length < 8) return setErr("비밀번호는 8자 이상이어야 합니다.");
    if (!license) return setErr("사업자등록증을 첨부해 주세요.");
    if (license.size > 3 * 1024 * 1024) return setErr("사업자등록증 파일은 3MB 이하로 업로드해 주세요.");

    setBusy(true);
    try {
      const license_blob = await fileToBase64(license);
      await api.post("/auth/signup", {
        email: form.email, password: form.password,
        company_name: form.company_name, business_no: form.business_no,
        representative: form.representative, phone: form.phone, address: form.address,
        license_blob,
      });
      setDone(true);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="container" style={{ maxWidth: 520, padding: "60px 20px" }}>
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h1 className="h-title">가입 신청 완료</h1>
          <p className="muted" style={{ marginTop: 12, marginBottom: 24 }}>
            사업자등록증 확인 후 영업일 1일 이내에 승인 결과를 이메일로 안내드립니다.<br />
            승인 완료 시 로그인하여 이용하실 수 있습니다.
          </p>
          <button className="btn btn-primary" onClick={() => nav("/")}>메인으로</button>
        </div>
      </div>
    );
  }

  return (
    <main id="main-content" className="container" style={{ maxWidth: 640, padding: "var(--s-8) var(--s-5)" }}>
      <p className="eyebrow" style={{ marginBottom: 4 }}>Business Signup</p>
      <h1 className="h-title" style={{ marginBottom: 6 }}>사업자 회원가입</h1>
      <p className="muted" style={{ marginBottom: 20, fontSize: 15 }}>가입 신청 후 관리자 승인을 거쳐야 이용하실 수 있습니다.</p>
      <div className="card" style={{ padding: "var(--s-6)" }}>
        <form onSubmit={submit} className="stack">
          <section>
            <h3 className="h-section">계정 정보</h3>
            <div className="stack">
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>이메일 *</div>
                <input required type="email" value={form.email} onChange={update("email")} />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>비밀번호 * (8자 이상)</div>
                  <input required type="password" value={form.password} onChange={update("password")} />
                </label>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>비밀번호 확인 *</div>
                  <input required type="password" value={form.password2} onChange={update("password2")} />
                </label>
              </div>
            </div>
          </section>

          <section style={{ paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <h3 className="h-section">사업자 정보</h3>
            <div className="stack">
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>상호(업체명) *</div>
                  <input required value={form.company_name} onChange={update("company_name")} />
                </label>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>사업자등록번호 *</div>
                  <input required placeholder="000-00-00000" value={form.business_no} onChange={update("business_no")} />
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>대표자 *</div>
                  <input required value={form.representative} onChange={update("representative")} />
                </label>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>연락처 *</div>
                  <input required placeholder="010-0000-0000" value={form.phone} onChange={update("phone")} />
                </label>
              </div>
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>사업장 주소</div>
                <input value={form.address} onChange={update("address")} />
              </label>
            </div>
          </section>

          <section style={{ paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <h3 className="h-section">사업자등록증 첨부</h3>
            <label className="card" style={{ padding: 20, display: "block", cursor: "pointer", borderStyle: "dashed", textAlign: "center" }}>
              <input hidden type="file" accept="image/*,.pdf" onChange={(e) => setLicense(e.target.files?.[0] ?? null)} />
              {license ? (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>📎 {license.name}</div>
                  <div className="muted" style={{ fontSize: 15, marginTop: 4 }}>{(license.size / 1024).toFixed(0)} KB · 변경하려면 클릭</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 4 }}>📄</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>클릭하여 사업자등록증 업로드</div>
                  <div className="muted" style={{ fontSize: 15, marginTop: 4 }}>JPG, PNG, PDF · 최대 3MB</div>
                </div>
              )}
            </label>
          </section>

          {err && <div style={{ fontSize: 15, color: "var(--danger)", padding: "10px 14px", background: "var(--danger-soft)", borderRadius: "var(--r-sm)" }}>{err}</div>}

          <button className="btn btn-cta btn-lg" disabled={busy}>{busy ? "신청 중..." : "가입 신청"}</button>
          <div style={{ fontSize: 15, textAlign: "center" }}>
            이미 회원이세요? <Link to="/login" style={{ color: "var(--primary)", fontWeight: 600 }}>로그인</Link>
          </div>
        </form>
      </div>

      <style>{`
        @media (max-width: 540px) {
          main .stack > div[style*="1fr 1fr"],
          main .stack > div[style*="2fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </main>
  );
}
